/**
 * Thirteen original marks, one per shelf and mood.
 *
 * Every motif is built from primitives inside a single 48x48 box: no traced
 * artwork, no icon font, no downloaded image. Each draws in two inks - the
 * shelf's accent (`currentColor`) and a paper tone - so a card can be tinted
 * by changing one custom property.
 *
 * They are deliberately drawn as *game pieces* rather than as UI glyphs: a
 * counter, a card corner, a joystick ball, a checkered flag. That is the
 * vocabulary the rest of the portal is built from.
 */

import type { MotifId } from "@kikik/game-catalog";

export interface MotifProps {
  motif: MotifId;
  /** Decorative by default; give a label only when the mark carries meaning. */
  label?: string;
  className?: string;
}

function paths(motif: MotifId) {
  switch (motif) {
    case "mahjong-tiles":
      return (
        <>
          <rect x="4" y="12" width="17" height="26" rx="4" className="motif__paper" />
          <rect x="4" y="12" width="17" height="26" rx="4" className="motif__line" />
          <circle cx="12.5" cy="21" r="3" className="motif__ink" />
          <circle cx="12.5" cy="30" r="3" className="motif__ink" />
          <rect x="24" y="8" width="17" height="26" rx="4" className="motif__paper" />
          <rect x="24" y="8" width="17" height="26" rx="4" className="motif__line" />
          <rect x="29" y="14" width="7" height="14" rx="3.5" className="motif__ink" />
        </>
      );
    case "board":
      return (
        <>
          <rect x="6" y="6" width="36" height="36" rx="6" className="motif__paper" />
          <rect x="6" y="6" width="36" height="36" rx="6" className="motif__line" />
          <path d="M18 6v36M30 6v36M6 18h36M6 30h36" className="motif__line" />
          <circle cx="12" cy="12" r="4" className="motif__ink" />
          <circle cx="36" cy="36" r="4" className="motif__ink" />
        </>
      );
    case "puzzle":
      return (
        <>
          <path
            d="M8 10h12a4 4 0 0 1 8 0h12v12a4 4 0 0 0 0 8v12H28a4 4 0 0 0-8 0H8V30a4 4 0 0 1 0-8Z"
            className="motif__paper"
          />
          <path
            d="M8 10h12a4 4 0 0 1 8 0h12v12a4 4 0 0 0 0 8v12H28a4 4 0 0 0-8 0H8V30a4 4 0 0 1 0-8Z"
            className="motif__line"
          />
          <circle cx="30" cy="20" r="3" className="motif__ink" />
        </>
      );
    case "cards":
      return (
        <>
          <rect
            x="8"
            y="10"
            width="20"
            height="28"
            rx="4"
            className="motif__paper"
            transform="rotate(-12 18 24)"
          />
          <rect
            x="8"
            y="10"
            width="20"
            height="28"
            rx="4"
            className="motif__line"
            transform="rotate(-12 18 24)"
          />
          <rect x="22" y="10" width="20" height="28" rx="4" className="motif__paper" />
          <rect x="22" y="10" width="20" height="28" rx="4" className="motif__line" />
          <path d="M32 18l5 6-5 6-5-6Z" className="motif__ink" />
        </>
      );
    case "arcade":
      return (
        <>
          <path d="M24 12v14" className="motif__line" strokeWidth="4" />
          <circle cx="24" cy="11" r="7" className="motif__ink" />
          <path d="M10 38a6 6 0 0 1 6-6h16a6 6 0 0 1 6 6Z" className="motif__paper" />
          <path d="M10 38a6 6 0 0 1 6-6h16a6 6 0 0 1 6 6Z" className="motif__line" />
        </>
      );
    case "bolt":
      return (
        <>
          <path d="M27 5 12 27h9l-3 16 18-24h-10l3-14Z" className="motif__paper" />
          <path d="M27 5 12 27h9l-3 16 18-24h-10l3-14Z" className="motif__line" />
          <path d="M21 27h6l-2 8Z" className="motif__ink" />
        </>
      );
    case "compass":
      return (
        <>
          <circle cx="24" cy="24" r="17" className="motif__paper" />
          <circle cx="24" cy="24" r="17" className="motif__line" />
          <path d="M31 17 26 26l-9 5 5-9Z" className="motif__ink" />
          <circle cx="24" cy="24" r="2" className="motif__paper" />
        </>
      );
    case "flag":
      return (
        <>
          <path d="M13 8v34" className="motif__line" strokeWidth="4" />
          <path d="M13 9h26v16H13Z" className="motif__paper" />
          <path d="M13 9h13v8H13Zm13 8h13v8H26Z" className="motif__ink" />
          <path d="M13 9h26v16H13Z" className="motif__line" />
        </>
      );
    case "dial":
      return (
        <>
          <circle cx="24" cy="24" r="16" className="motif__paper" />
          <circle cx="24" cy="24" r="16" className="motif__line" />
          <path d="M24 10v4M38 24h-4M24 38v-4M10 24h4" className="motif__line" strokeWidth="3" />
          <path d="M24 24 33 17" className="motif__ink" strokeWidth="4" fill="none" />
          <circle cx="24" cy="24" r="3" className="motif__ink" />
        </>
      );
    case "confetti":
      return (
        <>
          <circle cx="12" cy="14" r="5" className="motif__ink" />
          <rect
            x="26"
            y="6"
            width="10"
            height="10"
            rx="2"
            className="motif__paper"
            transform="rotate(18 31 11)"
          />
          <rect
            x="26"
            y="6"
            width="10"
            height="10"
            rx="2"
            className="motif__line"
            transform="rotate(18 31 11)"
          />
          <path d="M8 40 20 26l6 6-12 12Z" className="motif__paper" />
          <path d="M8 40 20 26l6 6-12 12Z" className="motif__line" />
          <circle cx="36" cy="34" r="6" className="motif__ink" />
        </>
      );
    case "wave":
      return (
        <>
          <rect x="7" y="20" width="6" height="10" rx="3" className="motif__ink" />
          <rect x="17" y="12" width="6" height="26" rx="3" className="motif__paper" />
          <rect x="17" y="12" width="6" height="26" rx="3" className="motif__line" />
          <rect x="27" y="6" width="6" height="36" rx="3" className="motif__ink" />
          <rect x="37" y="18" width="6" height="14" rx="3" className="motif__paper" />
          <rect x="37" y="18" width="6" height="14" rx="3" className="motif__line" />
        </>
      );
    case "home":
      return (
        <>
          <path d="M24 6 43 22v20H5V22Z" className="motif__paper" />
          <path d="M24 6 43 22v20H5V22Z" className="motif__line" />
          <path d="M19 42V28h10v14Z" className="motif__ink" />
        </>
      );
    case "flask":
      return (
        <>
          <path d="M19 6h10v13l10 18a5 5 0 0 1-4 8H13a5 5 0 0 1-4-8l10-18Z" className="motif__paper" />
          <path d="M19 6h10v13l10 18a5 5 0 0 1-4 8H13a5 5 0 0 1-4-8l10-18Z" className="motif__line" />
          <circle cx="20" cy="34" r="3" className="motif__ink" />
          <circle cx="28" cy="38" r="2" className="motif__ink" />
          <path d="M17 6h14" className="motif__line" strokeWidth="4" />
        </>
      );
    default:
      return null;
  }
}

export function Motif({ motif, label, className }: MotifProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={`motif ${className ?? ""}`}
      data-motif={motif}
      role={label ? "img" : "presentation"}
      {...(label ? { "aria-label": label } : { "aria-hidden": true })}
      focusable="false"
    >
      {paths(motif)}
    </svg>
  );
}
